<template>
  <div class="bonus-programs">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true">
        <use xlink:href="#icon-CC" fill="#fff"></use>
      </svg>
    </breadcrumbs>

    <div >
      <div class="table-head">
        <a-space>
          <ButtonGroups
            :pagination="pagination"
            :tableData="dataSource"
            @pageChange="onPageChange"
          />
        </a-space>

        <a-space class="filters" >
          <a-input-search
            v-model:value="keyword"
            placeholder="Search Brand..."
            allow-clear
            @search="onSearch"
            @pressEnter="onSearch"
          />
          <div class="statusFilter">
            <span class="filter-label">Status</span>
            <artmisads-select
              v-model:value="status"
              style="width: 140px"
              popup-class-name="bonus-programs-status-dropdown"
              :get-popup-container="getSelectPopupContainer"
              @change="onSearch"
            >
              <a-select-option value="">All</a-select-option>
              <!-- <a-select-option value="INCOMING">Incoming</a-select-option> -->
              <a-select-option value="ACTIVE">Active</a-select-option>
              <a-select-option value="CANCELLED">Cancelled</a-select-option>
              <a-select-option value="COMPLETED">Completed</a-select-option>
            </artmisads-select>
          </div>
        </a-space>
      </div>

      <a-spin :spinning="load">
        <div v-if="dataSource.length" class="card-grid">
          <BonusProgramCard
            v-for="record in dataSource"
            :key="getProgramId(record)"
            :record="record"
            @view="onView"
            @open-campaigns="openCampaigns"
            @open-bonus-rule="openBonusRule"
          />
        </div>
        <a-empty v-else class="empty-state" description="No bonus programs found" />
      </a-spin>

      <!-- <div v-if="pagination.total > 0" class="card-pagination">
        <artmisads-pagination
          v-model:current="pagination.current"
          v-model:pageSize="pagination.pageSize"
          :total="pagination.total"
          :page-size-options="['9', '12', '18', '24']"
          show-size-changer
          @change="onPaginationChange"
        />
      </div> -->
    </div>

    <CampaignsModal ref="campaignsModalRef" />
    <BonusRuleModal ref="bonusRuleModalRef" />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import breadcrumbs from '~/pages/seller/breadcrumbs.vue'
import ButtonGroups from '~/pages/seller/Brands/components/buttonGroups.vue'
import { creatorConnectionsApi } from '@/server/index'

import BonusProgramCard from './components/bonusProgramCard.vue'
import CampaignsModal from './components/campaignsModal.vue'
import BonusRuleModal from './components/bonusRuleModal.vue'

const router = useRouter()

const load = ref(false)
const keyword = ref('')
const status = ref('')
const dataSource = ref([])

const getSelectPopupContainer = () => document.body

const campaignsModalRef = ref(null)
const bonusRuleModalRef = ref(null)

const breadcrumbsData = {
  title: 'Bonus Program',
  des: 'View ACC bonus programs from brands you work with.',
  hasTitleIcon: true,
  color: '#9C7AFB',
  links: [
    { name: 'Home', path: '/publisher/dashboard' },
    { name: 'Creator Connections', path: '/publisher/creatorConnections/bonusPrograms' },
    { name: 'Bonus Program', path: '/publisher/creatorConnections/bonusPrograms' },
  ],
}

const pagination = reactive({
  total: 0,
  current: 1,
  size: 9,
  pageSize: 9,
})

const getProgramId = (record) => record?.programId ?? record?.id

const getProgramList = async () => {
  load.value = true
  try {
    const res = await creatorConnectionsApi.getBonusProgramList({
      page: pagination.current,
      size: pagination.size,
      keyword: keyword.value || undefined,
      status: status.value || undefined,
    })
    if (res?.success && res?.data) {
      dataSource.value = res.data.data ?? []
      pagination.total = res.data.total ?? dataSource.value.length
    } else {
      dataSource.value = []
      pagination.total = 0
      if (res?.error) message.error(res.error)
    }
  } catch {
    dataSource.value = []
    pagination.total = 0
    message.error('Failed to load data')
  } finally {
    load.value = false
  }
}

const onPaginationChange = (current, pageSize) => {
  pagination.current = current
  pagination.size = pageSize ?? pagination.pageSize
  pagination.pageSize = pageSize ?? pagination.pageSize
  getProgramList()
}
const onPageChange = (current) => {
  pagination.current = current
  getProgramList()
}

const onSearch = () => {
  pagination.current = 1
  getProgramList()
}

const openCampaigns = (record) => campaignsModalRef.value?.show(record)

const openBonusRule = (record) => bonusRuleModalRef.value?.show(record)

const onView = (record) => {
  const id = getProgramId(record)
  if (!id) return
  router.push(`/publisher/creatorConnections/programdetails/${id}`)
}

onMounted(() => {
  getProgramList()
})
</script>

<style scoped lang="less">
.bonus-programs {
  padding-top: 0;
}

.content-wrapper {
  display: block !important;
  padding: 0 16px 16px;

  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
}

.table-head {
  display: flex;
  height: 64px;
  padding: 0 16px;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  margin: 16px 0;
  .filters {
    display: flex;
    align-items: center;
    gap: 20px !important;
  }

  .statusFilter {
    display: flex;
    align-items: center;
    position: relative;
  }

  .filter-label {
    margin-right: 8px;
    font-family: Sora, sans-serif;
    font-size: 14px;
    color: var(--dark-2);
  }
}

.card-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
}

.empty-state {
  padding: 48px 0;
}

.card-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e8e5ed;
  margin-top: 8px;
}

@media (max-width: 768px) {
  .table-head {
    height: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px 0;
  }
}
</style>

<style lang="less">
.bonus-programs-status-dropdown {
  z-index: 1100 !important;
}
</style>
